import React, { memo } from 'react';
import {
  Box,
  Typography,
  Divider,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { chartLegend } from '../config/settings';
import LegendItem from './LegendItem';
import IndicatorAttribution from './IndicatorAttribution';

/**
 * ChartLegend - Chart legend display component
 * Manages responsive display of legend elements and documentation link
 *
 * @param {Object} props - Component props
 * @param {Array} [props.legendItems] - Legend items to display
 * @param {boolean} [props.isReadingGuide] - Reading guide mode with full descriptions and balanced layout
 * @param {boolean} [props.showHowToRead] - Show "How to read" link
 * @param {string} [props.indicatorKey] - Indicator identifier for attribution
 * @returns {React.ReactElement} Chart legend with responsive layout
 */
const ChartLegend = ({
  legendItems = [],
  isReadingGuide = false,
  showHowToRead = true,
  indicatorKey,
}) => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.only('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Stack
        sx={{
          flexDirection: isSmallScreen || isMediumScreen ? 'column' : 'row',
          gap: 2,
          '@media print': {
            flexDirection: isReadingGuide ? 'column' : 'row',
            gap: 1,
          },
        }}
      >
        <Stack
          sx={{
            flexDirection: isVerySmallScreen ? 'column' : 'row',
            gap: isVerySmallScreen ? 2 : 4,
            '@media print': {
              flexDirection: 'row',
              gap: 4,
            },
          }}
          justifyContent={isReadingGuide ? 'flex-start' : 'space-around'}
          flexGrow={isReadingGuide ? 0 : 1}
          flexBasis={isReadingGuide ? '100%' : 'auto'}
        >
          {legendItems.slice(0, 2).map(legendItem => (
            <LegendItem
              key={legendItem.description}
              {...legendItem}
              description={isReadingGuide && legendItem.description}
            />
          ))}
        </Stack>
        <Stack
          sx={{
            flexDirection: isVerySmallScreen ? 'column' : 'row',
            gap: isVerySmallScreen ? 2 : 4,
            '@media print': {
              flexDirection: 'row',
              gap: 4,
            },
          }}
          justifyContent={isReadingGuide ? 'flex-start' : 'space-around'}
          flexGrow={isReadingGuide ? 0 : 1}
          flexBasis={isReadingGuide ? '100%' : 'auto'}
        >
          {legendItems.slice(2).map(legendItem => (
            <LegendItem
              key={legendItem.description}
              {...legendItem}
              description={isReadingGuide && legendItem.description}
            />
          ))}
        </Stack>
      </Stack>
      <Divider sx={{ my: 2, '@media print': { my: 1 } }} />

      {showHowToRead && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          sx={{ mb: 1 }}
        >
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Box component="a" href="#figures" sx={{ displayPrint: 'none'}}>
              <Typography variant="caption">{chartLegend.linkToHowToReadText}</Typography>
            </Box>
          </Box>
          <IndicatorAttribution
            indicatorKey={indicatorKey}
            sx={{ ml: 'auto !important'}}
          />
        </Stack>
      )}
    </>
  );
};

ChartLegend.displayName = 'ChartLegend';

export default memo(ChartLegend);
